import { useQuery } from '@apollo/client';
import { ApprovalStatus } from 'graphql-query-contracts';
import { getSubmissionVisitors } from 'graphql-queries';

export interface SubmissionVisitor {
  id: string;
  visitorPass?: {
    visitor: {
      firstName: string;
      lastName: string;
      company: string;
      email: string;
      phone: string;
    };
    hostName: string;
    host?: {
      profile: {
        email: string;
        phone: string;
        image: string;
      };
    };
    tenantName: string;
    tenant?: {
      name: string;
      address: {
        street1: string;
        street2: string;
        city: string;
        state: string;
      };
      profile: {
        logo: string;
        image: string;
      };
    };
    floor: string;
    startDatetime: string;
    endDatetime: string;
  };
  recurrence?: {
    recurrenceId: string;
    schedule: string;
    scheduleDuration: string;
  };
  watchlists?: Array<{
    id: string;
    organizationId: string;
    visitorMatchingInfo: {
      firstName?: {
        primaryValue: string;
        alternateValues: string[];
        matchType: string;
      };
      lastName?: {
        primaryValue: string;
        alternateValues: string[];
        matchType: string;
      };
      email?: {
        primaryValue: string;
        alternateValues: string[];
        matchType: string;
      };
      phone?: {
        primaryValue: string;
        alternateValues: string[];
        matchType: string;
      };
    };
    severityLevel: string;
    notes: string;
    attachmentIds: string[];
  }>;
}

interface SubmissionVisitorsResponse {
  submissionVisitors: {
    submissionVisitors: SubmissionVisitor[];
    totalCount: number;
  };
}

interface UsePendingApprovalsParams {
  organizationId: string | undefined;
}

interface UsePendingApprovalsResult {
  pendingVisitors: SubmissionVisitor[];
  pendingCount: number;
  loading: boolean;
  refetch: () => void;
}

export function usePendingApprovals({
  organizationId,
}: UsePendingApprovalsParams): UsePendingApprovalsResult {
  const { data, loading, refetch } = useQuery<SubmissionVisitorsResponse>(
    getSubmissionVisitors,
    {
      variables: {
        filter: {
          organizationId,
          approvalStatus: ApprovalStatus.ApprovalStatusPending,
        },
      },
      skip: !organizationId,
      fetchPolicy: 'network-only',
    }
  );

  const pendingVisitors = data?.submissionVisitors?.submissionVisitors || [];
  const pendingCount = data?.submissionVisitors?.totalCount || 0;

  return {
    pendingVisitors,
    pendingCount,
    loading,
    refetch,
  };
}
